/*
 * Copyright (c) 2024 MTa Learning. All Rights Reserved.
 *
 * MTa Learning proprietary information: the enclosed materials contain proprietary information of MTa Learning and
 * shall not be disclosed in whole or in any part to any third party or used by any person for any purpose, without
 * written consent of MTa Learning. Duplication of any portion of these materials shall include this notice.
 */
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:mta-learning":true});}catch(e){}}();import React, { createElement, DetailedHTMLProps, forwardRef, InputHTMLAttributes, useMemo, useState } from 'react'
import { MdOutlineVisibility, MdOutlineVisibilityOff } from 'react-icons/md'

import { cn } from '@/utils/css'

/** Description of available props for {@link TextField} component. */
type TextFieldProps = Omit<
  DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'name' | 'type' | 'aria-invalid' | 'aria-describedby' | 'ref'
> & {
  /** Name of the input field. */
  name: string

  /** Label for the input field. */
  label?: string

  /** The type of the input field. */
  type?: 'text' | 'password' | 'email'

  /** Helper text for the input field. */
  helperText?: string

  /** Error text for the input field. */
  errorText?: string

  /** Whether the field has been dirtied. */
  dirty?: boolean

  /** A leading icon to display in the input field. */
  leadingIcon?: React.ElementType
}

/**
 * Text field component used to render a text input field with a label, helper text and error text.
 */
const TextField: React.FC<TextFieldProps> = forwardRef<HTMLInputElement, TextFieldProps>(
  ({ name, label, type = 'text', helperText, errorText, required, dirty, leadingIcon, disabled, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false)
    const fieldType = useMemo(() => (type === 'password' && showPassword ? 'text' : type), [type, showPassword])

    return (
      <>
        {label && (
          <>
            <label
              htmlFor={name}
              className={cn(
                'block text-sm font-light text-graphite-500',
                dirty && 'text-blue-600',
                !!errorText && 'text-danger-300',
              )}
            >
              {label}
              {required && <span className={'text-danger-300'}> &#42;</span>}
            </label>
          </>
        )}
        <div className={'relative mt-2 rounded-md shadow-xs'}>
          <div className={'pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3'}>
            {leadingIcon &&
              createElement(leadingIcon, {
                className: cn('h-5 w-5 text-graphite-500', dirty && 'text-blue-600', !!errorText && 'text-danger-300'),
              })}
          </div>
          <input
            ref={ref}
            type={fieldType}
            name={name}
            id={name}
            className={cn(
              'mt-1 px-4 py-2 block w-full bg-transparent rounded-lg shadow-xs text-graphite-500 border-0',
              'ring-1 ring-inset ring-graphite-500 focus:ring-inset focus:ring-2 focus:ring-graphite-500',
              dirty && 'text-blue-600 ring-blue-600 focus:ring-blue-600',
              !!errorText && 'text-danger-300 ring-danger-300 focus:ring-danger-300',
              leadingIcon && 'pl-10',
              type === 'password' && 'pr-10',
              disabled && 'cursor-not-allowed ring-graphite-300 text-graphite-300',
            )}
            aria-invalid={!!errorText}
            aria-describedby={errorText ? `${name}-error` : helperText ? `${name}-description` : undefined}
            disabled={disabled}
            {...props}
          />
          {type === 'password' && (
            <div className={'cursor-pointer absolute inset-y-0 right-0 flex items-center pr-3'}>
              {createElement(showPassword ? MdOutlineVisibility : MdOutlineVisibilityOff, {
                className: cn('w-5 h-5 text-graphite-500', dirty && 'text-blue-600', !!errorText && 'text-danger-300'),
                onClick: () => setShowPassword(!showPassword),
              })}
            </div>
          )}
        </div>
        {helperText && !errorText && (
          <p className={'mt-2 text-sm text-gray-500'} id={`${name}-description`}>
            {helperText}
          </p>
        )}
        {errorText && (
          <p className={'mt-2 text-sm text-danger-300'} id={`${name}-error`}>
            {errorText}
          </p>
        )}
      </>
    )
  },
)

TextField.displayName = 'TextField'

export { TextField }
export type { TextFieldProps }
